import React from 'react';
import {
  Table,
  TableBody,
} from '@/components/ui/table';
import type { UnifiedAsset } from '@/hooks/useAssetsUnified';
import type { AssetsSortConfig } from '@/hooks/useAssetsUnifiedData';

// Extracted components
import { AssetsTableHeader } from './AssetsTableHeader';
import { AssetsTableRow } from './AssetsTableRow';
import { InlineAddAssetRow } from './InlineAddAssetRow';

// Extracted logic hook
import { useAssetsTableLogic } from '@/hooks/useAssetsTableLogic';

interface AssetsTableProps {
  assets: UnifiedAsset[];
  allAssets?: UnifiedAsset[];
  sortConfig: AssetsSortConfig;
  onSort: (field: AssetsSortConfig['field']) => void;
  onEdit: (asset: UnifiedAsset) => void;
  selectedAssets: string[];
  onSelectionChange: (assetIds: string[]) => void;
  isLoading?: boolean;
  onAssetAdded?: () => void;
  onViewDetails?: (asset: UnifiedAsset) => void;
  onDelete?: (asset: UnifiedAsset) => void;
}

export function AssetsTable({ 
  assets, 
  allAssets,
  sortConfig, 
  onSort,
  onEdit, 
  selectedAssets,
  onSelectionChange,
  isLoading = false,
  onAssetAdded,
  onViewDetails,
  onDelete
}: AssetsTableProps) {
  const tableLogic = useAssetsTableLogic({
    assets,
    allAssets,
    sortConfig,
    onSort,
    onEdit,
    selectedAssets,
    onSelectionChange,
    onViewDetails
  });

  const {
    isAllSelected,
    isIndeterminate,
    handleSelectAll,
    handleSelectAsset,
    handleRowClick,
    handleViewDetails,
    handleEditAsset,
    handleSort,
    getSortIcon,
    getAssetTypeColor,
    openAssociatedAccount
  } = tableLogic;

  if (isLoading) {
    return (
      <div className="rounded-md border">
        <div className="p-8 text-center text-muted-foreground">
          Loading assets...
        </div>
      </div>
    );
  }

  // Always render the table so that inline add remains available

  return (
    <div className="w-full rounded-md border">
      <div className="w-full overflow-auto">
        <Table>
          <AssetsTableHeader
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
            onSelectAll={handleSelectAll}
            onSort={handleSort}
            getSortIcon={getSortIcon}
          />
          <TableBody className="[&_tr]:border-0">
            {assets && assets.length > 0 && assets.map((asset) => (
              <AssetsTableRow
                key={asset.id}
                asset={asset}
                isSelected={selectedAssets.includes(asset.id)}
                onSelectAsset={handleSelectAsset}
                onRowClick={handleRowClick}
                onViewDetails={handleViewDetails}
                onEdit={handleEditAsset}
                onDelete={onDelete}
                getAssetTypeColor={getAssetTypeColor}
                onOpenAssociatedAccount={openAssociatedAccount}
              />
            ))}
            {/* Inline add row at bottom */}
            {onAssetAdded && (
              <InlineAddAssetRow onAssetAdded={onAssetAdded} />
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
